/**
 * 文件路径: frontend/shared/api/subsidyTransfer.ts
 * 版本: v1.0
 * 创建日期: 2025-12-22
 * 
 * 补贴转赠 API（共享层）
 * 统一提供补贴余额转赠的相关接口
 * 适用于：运营端、企业端、员工小程序端
 */

import { httpClient } from './http'

// ========================================
// 类型定义
// ========================================

/**
 * 可转赠用户
 */
export interface TransferableUser {
  id: number
  username: string
  name: string
  phone?: string
  workplace_id?: number
  workplace_name?: string
  department_name?: string
  avatar?: string
}

/**
 * 转赠配置
 */
export interface TransferConfig {
  order_transfer_enabled: boolean      // 是否开启订单转赠
  subsidy_transfer_enabled: boolean    // 是否开启补贴转赠
  transfer_scope: 'ENTERPRISE' | 'WORKPLACE'  // 转赠范围
  cross_workplace_transfer: boolean    // 是否允许跨职场转赠
  transfer_daily_limit?: number        // 每日转赠限额
  transfer_monthly_limit?: number      // 每月转赠限额
}

/**
 * 转赠统计
 */
export interface TransferStatistics {
  daily_sent: {
    amount: number
    count: number
    limit?: number
    remaining?: number
  }
  monthly_sent: {
    amount: number
    count: number
    limit?: number
    remaining?: number
  }
  monthly_received: {
    amount: number
    count: number
  }
}

/**
 * 补贴转赠记录
 */
export interface SubsidyTransfer {
  id: number
  transfer_no: string
  from_user: number
  from_user_name: string
  from_workplace_name?: string | null
  to_user: number
  to_user_name: string
  to_workplace_name?: string | null
  amount: string
  status: 'PENDING' | 'COMPLETED' | 'CANCELLED' | 'FAILED'
  status_display?: string
  remarks?: string | null
  created_at: string
  completed_at?: string
}

/**
 * 转赠记录列表查询参数
 */
export interface TransferListParams {
  page?: number
  page_size?: number
  search?: string
  status?: string
  direction?: 'sent' | 'received' | 'all'
  created_at_after?: string
  created_at_before?: string
  enterprise?: number | string
  /**
   * 输入：员工ID
   * 输出意义：用于管理端按员工维度过滤（from_user 或 to_user 命中）
   */
  user_id?: number
}

/**
 * 分页响应
 */
export interface PaginatedTransferResponse {
  count: number
  results: SubsidyTransfer[]
}

/**
 * 创建转赠请求
 */
export interface CreateTransferRequest {
  to_user: number          // 接收方用户ID
  amount: string | number  // 转赠金额
  remarks?: string         // 备注
}

// ========================================
// API 函数
// ========================================

/**
 * 获取转赠配置
 * 获取当前用户所属企业的转赠配置
 */
export async function getTransferConfig(): Promise<TransferConfig> {
  return httpClient.get<TransferConfig>('finance/subsidy-transfers/config/')
}

/**
 * 获取可转赠用户列表
 * @param search 搜索关键词（姓名、工号等）
 */
export async function getTransferableUsers(search?: string): Promise<TransferableUser[]> {
  const params: Record<string, string> = {}
  if (search) {
    params.search = search
  }
  return httpClient.get<TransferableUser[]>('finance/subsidy-transfers/transferable-users/', { params })
}

/**
 * 获取转赠统计
 * 获取当前用户的转赠统计数据
 */
export async function getTransferStatistics(): Promise<TransferStatistics> {
  return httpClient.get<TransferStatistics>('finance/subsidy-transfers/statistics/')
}

/**
 * 获取转赠记录列表
 * @param params 查询参数
 */
export async function listTransfers(params?: TransferListParams): Promise<PaginatedTransferResponse> {
  return httpClient.get<PaginatedTransferResponse>('finance/subsidy-transfers/', { params })
}

/**
 * 获取转赠记录详情
 * @param id 转赠记录ID
 */
export async function getTransferDetail(id: number | string): Promise<SubsidyTransfer> {
  return httpClient.get<SubsidyTransfer>(`finance/subsidy-transfers/${id}/`)
}

/**
 * 创建补贴转赠
 * @param data 转赠数据
 */
export async function createTransfer(data: CreateTransferRequest): Promise<SubsidyTransfer> {
  return httpClient.post<SubsidyTransfer>('finance/subsidy-transfers/', data)
}

/**
 * 取消转赠（如果支持）
 * @param id 转赠记录ID
 */
export async function cancelTransfer(id: number | string): Promise<SubsidyTransfer> {
  return httpClient.post<SubsidyTransfer>(`finance/subsidy-transfers/${id}/cancel/`)
}

// ========================================
// 辅助函数
// ========================================

/**
 * 获取状态标签类型
 */
export function getStatusTagType(status: string): 'success' | 'warning' | 'danger' | 'info' {
  const map: Record<string, 'success' | 'warning' | 'danger' | 'info'> = {
    COMPLETED: 'success',
    PENDING: 'warning',
    CANCELLED: 'info',
    FAILED: 'danger'
  }
  return map[status] || 'info'
}

/**
 * 获取状态显示文本
 */
export function getStatusLabel(status: string): string {
  const map: Record<string, string> = {
    PENDING: '处理中',
    COMPLETED: '已完成',
    CANCELLED: '已取消',
    FAILED: '失败'
  }
  return map[status] || status
}

